import { ChevronLeft, ChevronRight, Stadium } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Select, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonTableApp, TableCellApp, TableModuleApp, TypographyApp } from '../../components/custom/CustomComponents';
import { Context } from '../../service/Context';
import { capitalizar, formatFechaPartido, ordenarPartidosPorFecha } from '../../utils/UtilesComun';

/**
 * Columnas de la tabla de jornada.
 */
const columns = [
  {
    field: 'fecha',
    label: 'Fecha',
    width: '11%',
    tooltip: 'Fecha del partido',
    sortable: false,
    align: 'left',
    sx: { textAlign: 'left !important', pl: '18px !important' },
  },
  {
    field: 'hora',
    label: 'Hora',
    width: '6%',
    tooltip: 'Hora del partido (formato 24h)',
    sortable: false,
    align: 'center',
    sx: { textAlign: 'center !important' },
  },
  {
    field: 'local',
    label: 'Local',
    width: '21%',
    tooltip: 'Equipo local',
    sortable: false,
    align: 'right',
    sx: { textAlign: 'right !important', pr: '10px !important' },
  },
  {
    field: 'resultado',
    label: 'Res.',
    width: '7%',
    tooltip: 'Resultado del partido (clic para ver acta)',
    sortable: false,
    align: 'center',
    sx: { textAlign: 'center !important' },
  },
  {
    field: 'visitante',
    label: 'Visitante',
    width: '28%',
    tooltip: 'Equipo visitante',
    sortable: false,
    align: 'left',
    sx: { textAlign: 'left !important', pl: '8px !important' },
  },
  {
    field: 'campo',
    label: 'Campo',
    width: '27%',
    tooltip: 'Campo de juego y localidad',
    sortable: false,
    align: 'left',
    sx: { textAlign: 'left !important', pl: '4px !important' },
  },
];

/**
 * Fila de la tabla de jornada con detalles completos.
 */
const FilaJornada = ({ partido, columns }) => {
  const navigate = useNavigate();
  const infoFecha = formatFechaPartido(partido.fecha);

  return (
    <TableRow key={partido.idPartido} hover>
      {columns.map((col) => {
        if (col.field === 'fecha') {
          const textoFecha =
            infoFecha.diaSem && infoFecha.fecha && infoFecha.fecha !== '-'
              ? `${infoFecha.diaSem} ${infoFecha.fecha}`
              : infoFecha.fecha !== '-'
                ? infoFecha.fecha
                : '-';

          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, pl: '18px !important', pr: 0.5, textAlign: 'left', width: col.width }}
            >
              {textoFecha !== '-' ? (
                <Tooltip title={infoFecha.completa} arrow>
                  <TypographyApp sx={{ fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {textoFecha}
                  </TypographyApp>
                </Tooltip>
              ) : (
                <TypographyApp sx={{ fontSize: '0.74rem', color: 'text.disabled' }}>-</TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'hora') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, px: 0.5, textAlign: 'center', width: col.width }}>
              {infoFecha.hora ? (
                <Tooltip title={infoFecha.completa} arrow>
                  <TypographyApp
                    sx={{ fontSize: '0.74rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {infoFecha.hora}
                  </TypographyApp>
                </Tooltip>
              ) : (
                <TypographyApp sx={{ fontSize: '0.74rem', color: 'text.disabled' }}>-</TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'local') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.5,
                pl: 0.5,
                pr: '10px !important',
                overflow: 'hidden',
                width: col.width,
                textAlign: 'right',
              }}
            >
              <ButtonTableApp
                onClick={() => partido.idLocal && navigate(`/equipo/${partido.idLocal}`)}
                text={capitalizar(partido.nombreLocal || '')}
                tooltip={capitalizar(partido.nombreLocal || '')}
                sx={{
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  px: 0.25,
                  '& .MuiTypography-root': { textAlign: 'right' },
                }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'resultado') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, px: 0.5, textAlign: 'center', width: col.width }}>
              {partido.jugado ? (
                <Tooltip title="Ver acta del partido" arrow>
                  <Box
                    onClick={() => navigate(`/partido/${partido.idPartido}`)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 44,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      backgroundColor: '#f1f5f9',
                      border: '1px solid #cbd5e1',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: '#1e293b',
                      cursor: 'pointer',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#e2e8f0',
                        borderColor: '#94a3b8',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    {partido.golesLocal ?? '-'} - {partido.golesVisitante ?? '-'}
                  </Box>
                </Tooltip>
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 44,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: '#94a3b8',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                    userSelect: 'none',
                  }}
                >
                  -
                </Box>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'visitante') {
          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, pl: '8px !important', pr: 0.5, overflow: 'hidden', width: col.width, textAlign: 'left' }}
            >
              <ButtonTableApp
                onClick={() => partido.idVisitante && navigate(`/equipo/${partido.idVisitante}`)}
                text={capitalizar(partido.nombreVisitante || '')}
                tooltip={capitalizar(partido.nombreVisitante || '')}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', px: 0.25 }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'campo') {
          const tieneCampo = Boolean(partido.campo);
          const tieneLocalidad = Boolean(partido.localidad);
          const textoCampo = tieneCampo
            ? capitalizar(partido.campo)
            : tieneLocalidad
              ? capitalizar(partido.localidad)
              : '';
          const tooltipUbicacion = [
            tieneCampo ? `Campo: ${capitalizar(partido.campo)}` : '',
            tieneLocalidad ? capitalizar(partido.localidad) : '',
            partido.provincia ? `(${partido.provincia})` : '',
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: '4px !important', pr: 1, width: col.width }}>
              {textoCampo ? (
                <Tooltip title={tooltipUbicacion} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                    <Stadium sx={{ fontSize: '0.95rem', color: 'text.secondary', flexShrink: 0 }} />
                    <TypographyApp
                      sx={{
                        fontSize: '0.74rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {textoCampo}
                    </TypographyApp>
                  </Box>
                </Tooltip>
              ) : (
                <TypographyApp sx={{ fontSize: '0.74rem', color: 'text.disabled', textAlign: 'center' }}>
                  -
                </TypographyApp>
              )}
            </TableCellApp>
          );
        }

        return <TableCellApp key={col.field}>-</TableCellApp>;
      })}
    </TableRow>
  );
};

const Jornada = ({ datosLiga, partidosJornada, jornadaInicial = 1, totalJornadas = 1, ultimaJornada }) => {
  const { obtenerPartidosJornada } = useContext(Context);
  const defJornada = jornadaInicial || ultimaJornada || 1;
  const maxJornadas = Math.max(totalJornadas || 0, ultimaJornada || 0, defJornada);

  const [numJornada, setNumJornada] = useState(defJornada);
  const [partidos, setPartidos] = useState(() => ordenarPartidosPorFecha(partidosJornada));

  // Caché en memoria para evitar peticiones repetidas y lograr navegación instantánea
  const cachePartidosRef = useRef({});

  useEffect(() => {
    cachePartidosRef.current = {};
  }, [datosLiga?.idLiga]);

  useEffect(() => {
    if (partidosJornada) {
      const ordenados = ordenarPartidosPorFecha(partidosJornada);
      cachePartidosRef.current[defJornada] = ordenados;
      setPartidos(ordenados);
    }
    if (numJornada !== defJornada) {
      setNumJornada(defJornada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defJornada, partidosJornada]);

  // Manejo de eventos de cambio de jornada optimizado con caché y ordenación cronológica
  async function clickSelectJornada(num) {
    if (1 <= num && num <= maxJornadas) {
      setNumJornada(num);
      if (cachePartidosRef.current[num]) {
        setPartidos(cachePartidosRef.current[num]);
      } else {
        const nuevosPartidos = await obtenerPartidosJornada(datosLiga.idLiga, num);
        const ordenados = ordenarPartidosPorFecha(nuevosPartidos);
        cachePartidosRef.current[num] = ordenados;
        setPartidos(ordenados);
      }
    }
  }

  const partidosOrdenados = useMemo(() => {
    return ordenarPartidosPorFecha(partidos);
  }, [partidos]);

  if (!datosLiga || !partidos || !maxJornadas) {
    return null;
  }

  const jornadas = [...Array(maxJornadas).keys()].map((i) => i + 1);

  const headerContent = (
    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
      <IconButton
        size="small"
        onClick={() => clickSelectJornada(numJornada - 1)}
        disabled={numJornada <= 1}
        aria-label="Jornada anterior"
        sx={{
          p: 0.25,
          width: 24,
          height: 24,
          color: 'text.primary',
          '&.Mui-disabled': { opacity: 0.3, color: 'text.disabled' },
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
        }}
      >
        <ChevronLeft sx={{ fontSize: '1.25rem' }} />
      </IconButton>
      <TypographyApp
        sx={{
          fontSize: '0.9rem',
          fontWeight: 400,
          color: 'text.primary',
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        Jornada
      </TypographyApp>
      <Select
        id="jornada-select"
        name="jornada"
        size="small"
        value={numJornada}
        onChange={(e) => clickSelectJornada(Number(e.target.value))}
        variant="outlined"
        sx={{
          height: 24,
          fontSize: '0.85rem',
          fontWeight: 400,
          backgroundColor: 'transparent',
          borderRadius: '5px',
          '& .MuiSelect-select': {
            py: 0,
            pl: 1,
            pr: '22px !important',
            display: 'flex',
            alignItems: 'center',
            lineHeight: '24px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.3)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0284c7',
            borderWidth: '1px',
          },
          '& .MuiSelect-icon': {
            fontSize: '1.05rem',
            right: 2,
            color: 'text.secondary',
          },
        }}
      >
        {jornadas.map((num) => (
          <MenuItem key={num} value={num} sx={{ fontSize: '0.85rem', minHeight: 28, py: 0.5 }}>
            {num}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        size="small"
        onClick={() => clickSelectJornada(numJornada + 1)}
        disabled={numJornada >= maxJornadas}
        aria-label="Jornada siguiente"
        sx={{
          p: 0.25,
          width: 24,
          height: 24,
          color: 'text.primary',
          '&.Mui-disabled': { opacity: 0.3, color: 'text.disabled' },
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
        }}
      >
        <ChevronRight sx={{ fontSize: '1.25rem' }} />
      </IconButton>
    </Box>
  );

  return (
    <TableModuleApp
      title={headerContent}
      columns={columns}
      data={partidosOrdenados}
      sx={{
        '& .MuiTableCell-head': {
          fontSize: '0.68rem',
          py: '6px',
        },
        '& .MuiTableCell-body': {
          py: '4px',
        },
        '& .MuiTableCell-root:first-of-type, & .MuiTableCell-head:first-of-type': {
          pl: '18px !important',
        },
        '& > div:first-of-type': {
          py: '5px',
          minHeight: '37px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      renderRow={(partido) => <FilaJornada key={partido.idPartido} partido={partido} columns={columns} />}
    />
  );
};

Jornada.propTypes = {
  datosLiga: PropTypes.object,
  partidosJornada: PropTypes.array,
  jornadaInicial: PropTypes.number,
  totalJornadas: PropTypes.number,
  ultimaJornada: PropTypes.number,
};

FilaJornada.propTypes = {
  partido: PropTypes.object,
  columns: PropTypes.array,
};

export default Jornada;
